
import React, { useState, useEffect } from 'react';
import { generateSafetyText } from '../services/geminiService';
import { Button } from './Button';
import { Bot, Sparkles, AlertTriangle, Loader2, X, WifiOff } from 'lucide-react';
import { ReportLanguage } from '../types';
import { useNetzDa } from './mobile/wizardLogik';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApply: (data: { title: string; observation: string; regulation: string }) => void;
  language?: ReportLanguage;
  appMode?: 'sigeko' | 'hse';
  initialText?: string; // NEW Prop
}

export const AIHelper: React.FC<Props> = ({ isOpen, onClose, onApply, language = 'de', appMode = 'sigeko', initialText = '' }) => {
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shortVersion, setShortVersion] = useState(true);
  const [result, setResult] = useState<{ title: string; observation: string; regulation: string } | null>(null);
  // Ohne Netz kein Vorschlag: Knopf gesperrt, Hinweis statt Fehlermeldung nach dem Tipp.
  const netzDa = useNetzDa();

  // Effect to load initial text when modal opens
  useEffect(() => {
    if (isOpen) {
      setKeyword(initialText);
      setResult(null);
      setError('');
    }
  }, [isOpen, initialText]);

  if (!isOpen) return null;

  const handleGenerate = async () => {
    if (!keyword.trim() || !netzDa) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      // Fix: Cast language to ReportLanguage to satisfy the function signature
      const data = await generateSafetyText(keyword, shortVersion, language as ReportLanguage, appMode as 'sigeko' | 'hse');
      setResult(data);
    } catch (err) {
      // Der Dienst liefert die Meldung fertig („KI nicht erreichbar – …“); das Stichwort bleibt stehen.
      setError(language === 'en'
        ? 'AI not reachable – please write the text yourself or try again later.'
        : (err instanceof Error && err.message) || 'KI nicht erreichbar.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyInternal = () => {
      if (result) {
          onApply(result);
          setResult(null);
          setKeyword('');
          setError('');
      }
  };

  const title = language === 'en' ? 'Text suggestion' : 'Textvorschlag';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 animate-fade-in backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 p-4 flex justify-between items-center text-white">
          <div className="flex items-center gap-2">
            <Bot className="w-6 h-6" />
            <h3 className="font-bold text-lg">{title}</h3>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          <p className="text-slate-600 text-sm mb-4">
            {language === 'en' 
                ? 'Describe the situation or defect briefly. The AI will formulate title, description and regulation.' 
                : 'Beschreiben Sie kurz die Situation oder den Mangel. Die KI formuliert Titel, Beschreibung und Vorschrift.'}
          </p>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">
                {language === 'en' ? 'Keyword / Situation' : 'Stichwort / Situation'}
            </label>
            <div className="flex gap-2">
              <textarea 
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleGenerate();
                  }
                }}
                placeholder={language === 'en' ? 'e.g. Missing safety glasses' : 'z.B. Keine Schutzbrille beim Flexen'}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
                rows={3}
                autoFocus
              />
              <Button variant="ai" onClick={handleGenerate} disabled={loading || !keyword.trim() || !netzDa}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : !netzDa ? <WifiOff className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
              </Button>
            </div>
            {!netzDa && (
              <p className="text-sm text-slate-500 flex items-center gap-2">
                <WifiOff className="w-4 h-4 shrink-0" />{language === 'en' ? 'AI needs a network connection' : 'KI braucht Netz'}
              </p>
            )}
            
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 select-none mt-2">
                <input type="checkbox" checked={shortVersion} onChange={(e) => setShortVersion(e.target.checked)} className="rounded text-violet-600" />
                {language === 'en' ? 'Short version (1-2 sentences)' : 'Kurzfassung (1-2 Sätze ohne Maßnahmen)'}
            </label>
          </div>

          {error && <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center"><AlertTriangle className="w-4 h-4 mr-2" />{error}</div>}

          {result && (
            <div className="mt-6 space-y-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
              <div>
                <span className="text-xs font-bold text-slate-500 uppercase">{language === 'en' ? 'Proposed Title' : 'Vorschlag Titel'}</span>
                <p className="text-slate-900 font-bold mt-1">{result.title}</p>
              </div>
              <div>
                <span className="text-xs font-bold text-slate-500 uppercase">{language === 'en' ? 'Proposed Observation' : 'Vorschlag Bemerkung'}</span>
                <p className="text-slate-800 mt-1 text-sm">{result.observation}</p>
              </div>
              <div>
                <span className="text-xs font-bold text-slate-500 uppercase">{language === 'en' ? 'Regulation / Basis' : 'Vorschrift / Grundlage'}</span>
                <div className="flex items-center mt-1">
                   <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded font-mono font-semibold">{result.regulation}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>{language === 'en' ? 'Cancel' : 'Abbrechen'}</Button>
          <Button variant="primary" disabled={!result} onClick={handleApplyInternal}>
            {language === 'en' ? 'Apply' : 'Übernehmen'}
          </Button>
        </div>
      </div>
    </div>
  );
};
